/**
 * GraphViewer Component
 *
 * Read-only, chrome-free rendering of a run's workflow graph for embedding in
 * the Angular Plan / Execute dashboards via a dedicated iframe.
 *
 * Communication flow:
 *   Angular → INIT { componentType: 'graphViewer', entityType: 'RUN', entityId: runId, authToken }
 *   → graphSource=versionGraph: getRunInfo(runId) → resolve version id → fetchWetlabVersionGraphSnapshot(versionId)
 *   → graphSource=executeGraph: getRunInfo(runId) → getRunGraphSnapshot(runId, 'executeGraph')
 *   → render GraphEditorProvider in read-only mode (no toolbar / panel / modals / context menu)
 *
 * Can also be used in embedded mode with pre-loaded snapshot (for RunAnalysis).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IconButton, Paper, Divider, Tooltip } from '@mui/material';
import { ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, CropFree as CropFreeIcon } from '@mui/icons-material';

import { useEditorCommunication } from '@hooks/useEditorCommunication';
import { getRunInfo } from '@services/runApi.service';
import { getRunGraphSnapshot } from '@services/runStageApi.service';
import { fetchWetlabVersionGraphSnapshot } from '@services/editorApi.service';
import type { RunInfoResponse } from '@services/runApi.service';
import { GraphEditorProvider } from '@/graph-react';
import { GraphEffects } from '@/graph-react/GraphEffects';
import type { WorkflowGraph } from '@/graph';
import { deriveBatches, filterSnapshotByClone, resolveActiveClone } from '@/graph';
import { useGraphEditorStore, wireGraphToStore } from '@/graph-store';
import type { GraphSnapshot } from '@/graph/domain/types';
import { BatchSelector } from './BatchSelector';
import styles from './GraphViewer.module.css';

// =============================================================================
// CONSTANTS
// =============================================================================

const COMM_CONFIG = {
  targetOrigin: '*',
  debug: false,
};

const VIEWPORT_PADDING_PX = 24;

// =============================================================================
// PROPS
// =============================================================================

interface GraphViewerProps {
  /** Pre-loaded graph snapshot (used in embedded mode, e.g., RunAnalysis) */
  snapshot?: GraphSnapshot | null;
  /** Callback when a node is selected */
  onNodeSelect?: (nodeId: string | null) => void;
  /** Callback when an edge is selected */
  onEdgeSelect?: (edgeId: string | null) => void;
  /** Whether to use iframe communication (default: true for standalone) */
  useIframeCommunication?: boolean;
  /** Read-only mode (default: true) */
  readOnly?: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

/** RunEntity.version may be a bare id string or a populated { _id } object. */
function resolveVersionId(
  version: string | { _id?: string | null } | null | undefined,
): string | null {
  if (!version) return null;
  return typeof version === 'string' ? version : version._id ?? null;
}

// =============================================================================
// COMPONENT
// =============================================================================

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';
type GraphSource = 'versionGraph' | 'executeGraph';

export const GraphViewer: React.FC<GraphViewerProps> = ({
  snapshot: preloadedSnapshot,
  onNodeSelect,
  onEdgeSelect,
  useIframeCommunication = true,
  readOnly = true,
}) => {
  const communication = useIframeCommunication ? useEditorCommunication(COMM_CONFIG) : null;
  const graphSource: GraphSource = React.useMemo(() => {
    if (typeof window === 'undefined') return 'versionGraph';
    const source = new URLSearchParams(window.location.search).get('graphSource');
    return source === 'executeGraph' ? 'executeGraph' : 'versionGraph';
  }, []);

  const [loadState, setLoadState] = useState<LoadState>(preloadedSnapshot ? 'loaded' : 'idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [graphSnapshot, setGraphSnapshot] = useState<GraphSnapshot | null>(preloadedSnapshot ?? null);
  const [runEntity, setRunEntity] = useState<RunInfoResponse['entity'] | null>(null);
  const graphRef = useRef<WorkflowGraph | null>(null);
  const zoomIn = useGraphEditorStore((s) => s.zoomIn);
  const zoomOut = useGraphEditorStore((s) => s.zoomOut);
  const zoomFit = useGraphEditorStore((s) => s.zoomFit);

  // ── Batch instance selection ──────────────────────────────────────────────
  // Batched runs clone the whole graph per instance, reusing ids and positions.
  // Rendering the raw snapshot stacks every instance on top of the others, so
  // narrow it to one instance before hydrating.
  const [selectedClone, setSelectedClone] = useState<number | null>(null);

  const batches = React.useMemo(
    () => deriveBatches(graphSnapshot, runEntity?.batchedNodeId),
    [graphSnapshot, runEntity?.batchedNodeId],
  );

  // Resolved during render, so the very first paint is already narrowed and a
  // selection left over from a previous graph falls back instead of rendering
  // an empty canvas.
  const cloneNumber = React.useMemo(
    () => resolveActiveClone(graphSnapshot, selectedClone),
    [graphSnapshot, selectedClone],
  );

  const viewSnapshot = React.useMemo(() => {
    if (!graphSnapshot) return null;
    if (cloneNumber == null) return graphSnapshot;
    return filterSnapshotByClone(graphSnapshot, cloneNumber);
  }, [graphSnapshot, cloneNumber]);

  // Guard against stale async callbacks after unmount / reinit
  const cancelledRef = useRef(false);

  const normalizeViewport = useCallback(() => {
    const graph = graphRef.current;
    if (!graph) return;

    const container = graph.container as HTMLDivElement | undefined;
    if (!container) return;

    const view = graph.getView();
    const scale = view.getScale();
    let translate = view.getTranslate();
    let nextTx = translate.x;
    let nextTy = translate.y;

    let bounds = graph.getGraphBounds();

    // Keep the graph in positive space so overflow can be reached via scroll.
    if (bounds.x < VIEWPORT_PADDING_PX) {
      nextTx += (VIEWPORT_PADDING_PX - bounds.x) / Math.max(scale, 0.0001);
    }
    if (bounds.y < VIEWPORT_PADDING_PX) {
      nextTy += (VIEWPORT_PADDING_PX - bounds.y) / Math.max(scale, 0.0001);
    }

    if (nextTx !== translate.x || nextTy !== translate.y) {
      view.setTranslate(nextTx, nextTy);
      translate = view.getTranslate();
      bounds = graph.getGraphBounds();
    }

    // If the graph is smaller than viewport, center it visually.
    const innerWidth = Math.max(container.clientWidth - VIEWPORT_PADDING_PX * 2, 0);
    const innerHeight = Math.max(container.clientHeight - VIEWPORT_PADDING_PX * 2, 0);

    if (innerWidth > 0 && bounds.width < innerWidth) {
      const targetX = VIEWPORT_PADDING_PX + (innerWidth - bounds.width) / 2;
      nextTx = translate.x + (targetX - bounds.x) / Math.max(scale, 0.0001);
    }

    if (innerHeight > 0 && bounds.height < innerHeight) {
      const targetY = VIEWPORT_PADDING_PX + (innerHeight - bounds.height) / 2;
      nextTy = translate.y + (targetY - bounds.y) / Math.max(scale, 0.0001);
    }

    if (nextTx !== translate.x || nextTy !== translate.y) {
      view.setTranslate(nextTx, nextTy);
    }

    // Start at the top-left scroll origin; remaining overflow is reachable.
    container.scrollLeft = 0;
    container.scrollTop = 0;
  }, []);

  // ── Fetch on every new INIT (iframe mode only) ────────────────────────────
  useEffect(() => {
    // In embedded mode with pre-loaded snapshot, skip API fetch
    if (!useIframeCommunication || preloadedSnapshot) {
      return;
    }

    if (!communication?.initPayload) return;

    const { entityId } = communication.initPayload;
    cancelledRef.current = false;

    setGraphSnapshot(null);
    setRunEntity(null);
    setLoadState('loading');
    setErrorMessage(null);

    (async () => {
      const runInfo = await getRunInfo(entityId, graphSource);
      if (cancelledRef.current) return;

      setRunEntity(runInfo.entity);

      let snapshot: GraphSnapshot;
      if (graphSource === 'executeGraph') {
        snapshot = await getRunGraphSnapshot(entityId, 'executeGraph');
      } else {
        const versionId = resolveVersionId(runInfo.entity.version);
        if (!versionId) {
          throw new Error('Run has no associated version to load a graph from.');
        }
        snapshot = await fetchWetlabVersionGraphSnapshot(versionId);
      }
      if (!snapshot) {
        throw new Error('Graph snapshot missing in response.');
      }
      return snapshot;
    })()
      .then((snapshot) => {
        if (cancelledRef.current) return;
        if (!snapshot) {
          throw new Error('Graph snapshot missing in response.');
        }
        setGraphSnapshot(snapshot);
        setLoadState('loaded');
      })
      .catch((err: Error) => {
        if (cancelledRef.current) return;
        console.error('[GraphViewer] Failed to load run graph:', err);
        setErrorMessage(err.message || 'Failed to load graph.');
        if (communication?.sendError) {
          communication.sendError('GRAPH_VIEWER_INIT_FAILED', err.message || 'Failed to load graph.', err);
        }
        setLoadState('error');
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [communication?.initPayload, communication?.initSequence, useIframeCommunication, preloadedSnapshot, graphSource]);

  useEffect(() => {
    if (loadState !== 'loaded') return;

    const run = () => normalizeViewport();
    run();

    window.addEventListener('resize', run);
    return () => {
      window.removeEventListener('resize', run);
    };
  }, [loadState, normalizeViewport]);

  // ── States ─────────────────────────────────────────────────────────────────
  // In iframe mode, wait for communication to be ready
  if (useIframeCommunication && communication && (!communication.isReady || loadState === 'idle')) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.spinner} />
          <span>Connecting…</span>
        </div>
      </div>
    );
  }

  if (loadState === 'loading') {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.spinner} />
          <span>Loading graph…</span>
        </div>
      </div>
    );
  }

  if (loadState === 'error' || !graphSnapshot || !viewSnapshot) {
    return (
      <div className={styles.container}>
        <div className={styles.errorWrapper}>
          <p className={styles.errorTitle}>Failed to load graph</p>
          <p className={styles.errorMessage}>{errorMessage}</p>
        </div>
      </div>
    );
  }

  // ── Read-only graph ──────────────────────────────────────────────────────────
  return (
    <div className={styles.container}>
      <BatchSelector batches={batches} value={cloneNumber} onChange={setSelectedClone} />
      <GraphEditorProvider
        // cloneNumber is part of the key so switching batches remounts and
        // re-hydrates from scratch, rather than layering the new instance
        // onto the previous one's cells.
        key={`${communication?.initPayload?.entityId ?? 'embedded'}:${graphSource}:${cloneNumber ?? 'all'}`}
        config={{ readOnly }}
        initialSnapshot={viewSnapshot}
        onReady={(graph) => {
          graphRef.current = graph;
          wireGraphToStore(graph);
          // Seed the store so cycle-bracket visualization (GraphEffects) has data.
          // Must be the same snapshot the canvas was hydrated from, or the store
          // and the canvas disagree about which cells exist.
          useGraphEditorStore.getState().seedStoreState(viewSnapshot);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => normalizeViewport());
          });
        }}
        containerClassName="graph-canvas-scroll"
        containerStyle={{
          position: 'absolute',
          inset: 0,
          overflowX: 'auto',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        <GraphEffects enableAutoCenter={false} />
      </GraphEditorProvider>
      <Paper
        elevation={2}
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 1,
          overflow: 'hidden',
          zIndex: 10,
        }}
      >
        <Tooltip title="Zoom In" placement="left">
          <IconButton size="small" onClick={zoomIn} sx={{ borderRadius: 0 }}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem />
        <Tooltip title="Zoom Out" placement="left">
          <IconButton size="small" onClick={zoomOut} sx={{ borderRadius: 0 }}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem />
        <Tooltip title="Fit to Window" placement="left">
          <IconButton size="small" onClick={zoomFit} sx={{ borderRadius: 0 }}>
            <CropFreeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </div>
  );
};
